import axios from 'axios';

// Clean the URL - remove any trailing braces or whitespace
const getBackendURL = () => {
  const url = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
  // Remove trailing braces, whitespace, and slashes that shouldn't be there
  return url.replace(/[}\s/]+$/, '').replace(new RegExp('/$'), '');
};

const API_URL = getBackendURL();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    console.warn('⚠️ No token found in localStorage for API request to', config.url);
  }
  return config;
});

// Handle 401 errors - token expired or invalid
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('❌ 401 Unauthorized - Token may be expired or invalid');
      console.log('   Current token:', localStorage.getItem('token') ? 'exists' : 'missing');
      // Clear invalid token
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Redirect to login if not already there
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export interface LoginResponse {
  access_token: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    role: string;
    settings?: UserSettings | null;
    activeInterviewId?: string | null;
  };
}

export interface UserSettings {
  id: string;
  userId: string;
  systemPrompt: string;
  systemPromptForScreenshotAnalysis: string;
  startAudioCaptureAutomatically: boolean;
  answerQuestionsAutomatically: boolean;
  suggestQuestionsAutomatically: boolean;
  shouldSummarizeMessagesBeforeAnswer: boolean;
  language: string;
  interviewLanguage: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeepgramLanguageOption {
  name: string;
  code: string;
}

export interface GlobalSettings {
  id: string;
  completionsModel: string;
  sttModel: string;
  turnDetectionThreshold: number;
  turnDetectionPrefixPadding: number;
  turnDetectionSilenceDuration: number;
  assistUsingFullConversation: boolean;
  transcriptionModelDeepgram: string;
  transcriptionLanguageDeepgram: string;
  transcriptionModels?: string[];
  supportedLanguages?: DeepgramLanguageOption[];
  supportedLanguagesByModel?: Record<string, DeepgramLanguageOption[]>;
  createdAt: string;
  updatedAt: string;
}

export interface UserPortfolio {
  id: string;
  userId: string;
  cvFileUrl: string | null;
  cvFileName: string | null;
  cvUpdatedAt: string | null;
  cvSummary: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Interview {
  id: string;
  userId: string;
  messages: Message[];
  status: 'received' | 'processing' | 'completed';
  systemPromptForScreenshotAnalysis?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Message {
  id: string;
  interviewId: string;
  role: string;
  content: string;
  imageUrls: string[]; // Array of image URLs
  status: string; // 'streaming' | 'complete'
  isSummarized: boolean; // true when content was summarized (e.g. summarizeFirst on assist)
  createdAt: string;
}

export const authApi = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    console.log('📡 API Login request:');
    console.log('   URL:', api.defaults.baseURL + '/auth/login');
    console.log('   Email:', email);
    
    try {
      const response = await api.post<LoginResponse>('/auth/login', {
        email,
        password,
      });
      console.log('✅ Login API response:', response.status, response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Login API error:');
      console.error('   Status:', error.response?.status);
      console.error('   Data:', error.response?.data);
      console.error('   Message:', error.message);
      throw error;
    }
  },
};

export const interviewsApi = {
  getAllInterviews: async (): Promise<Interview[]> => {
    const response = await api.get<Interview[]>('/interviews');
    return response.data;
  },

  createInterview: async (
    jobDescription?: string,
    cvFile?: File,
    systemPromptForScreenshotAnalysis?: string,
  ): Promise<Interview | { success: boolean; interviewId: string; messageId: string; status: string; message?: string }> => {
    const formData = new FormData();
    if (jobDescription) {
      formData.append('jobDescription', jobDescription);
    }
    if (systemPromptForScreenshotAnalysis?.trim()) {
      formData.append('systemPromptForScreenshotAnalysis', systemPromptForScreenshotAnalysis.trim());
    }
    if (cvFile) {
      formData.append('cv', cvFile);
    }

    const response = await api.post<Interview | { success: boolean; interviewId: string; messageId: string; status: string; message?: string }>('/interviews', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getInterviewById: async (interviewId: string): Promise<Interview> => {
    const response = await api.get<Interview>(`/interviews/${interviewId}`);
    return response.data;
  },

  continueInterview: async (interviewId: string, message: string): Promise<{ success: boolean; interviewId: string; status: string; message?: string }> => {
    const response = await api.post<{ success: boolean; interviewId: string; status: string; message?: string }>(`/interviews/${interviewId}/continue`, {
      message,
    });
    return response.data;
  },

  assist: async (interviewId: string, message: string, files?: File[]): Promise<{ success: boolean; interviewId: string; messageId: string; status: string; message?: string }> => {
    const formData = new FormData();
    formData.append('interviewId', interviewId);
    formData.append('message', message);
    if (files && files.length > 0) {
      // Append all files with the same field name 'files' for array handling
      files.forEach((file) => {
        formData.append('files', file);
      });
    }
    const response = await api.post<{ success: boolean; interviewId: string; messageId: string; status: string; message?: string }>('/interviews/assist', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  cancelAssist: async (interviewId: string): Promise<{ success: boolean; interviewId: string; messageId: string; status: string; message?: string }> => {
    const response = await api.post<{ success: boolean; interviewId: string; messageId: string; status: string; message?: string }>('/interviews/assistcancel', {
      interviewId,
    });
    return response.data;
  },

  deleteInterview: async (interviewId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete<{ success: boolean; message: string }>(`/interviews/${interviewId}`);
    return response.data;
  },

  deleteAllInterviews: async (): Promise<{ success: boolean; message: string; count: number }> => {
    const response = await api.delete<{ success: boolean; message: string; count: number }>('/interviews');
    return response.data;
  },
};

export const userSettingsApi = {
  getUserSettings: async (): Promise<UserSettings> => {
    const response = await api.get<UserSettings>('/user-settings');
    return response.data;
  },

  updateUserSettings: async (settings: Partial<UserSettings>): Promise<UserSettings> => {
    const response = await api.put<UserSettings>('/user-settings', settings);
    return response.data;
  },
};

export const globalSettingsApi = {
  getGlobalSettings: async (): Promise<GlobalSettings> => {
    const response = await api.get<GlobalSettings>('/global-settings');
    return response.data;
  },

  getAvailableModels: async (): Promise<{ completionsModels: string[]; sttModels: string[] }> => {
    const response = await api.get<{ completionsModels: string[]; sttModels: string[] }>('/global-settings/available-models');
    return response.data;
  },

  updateGlobalSettings: async (settings: Partial<GlobalSettings>): Promise<GlobalSettings> => {
    const response = await api.put<GlobalSettings>('/global-settings', settings);
    return response.data;
  },
};

export const usersApi = {
  getPortfolio: async (): Promise<UserPortfolio> => {
    const response = await api.get<UserPortfolio>('/users/portfolio');
    return response.data;
  },

  uploadCV: async (file: File): Promise<UserPortfolio> => {
    const formData = new FormData();
    formData.append('cv', file);
    const response = await api.post<UserPortfolio>('/users/portfolio/cv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteCV: async (): Promise<{ success: boolean; message: string; portfolio: UserPortfolio }> => {
    const response = await api.delete<{ success: boolean; message: string; portfolio: UserPortfolio }>('/users/portfolio/cv');
    return response.data;
  },

  listUsers: async (): Promise<Array<{ id: string; email: string; name: string | null; role: 'USER' | 'ADMIN'; createdAt: string }>> => {
    const response = await api.get<Array<{ id: string; email: string; name: string | null; role: 'USER' | 'ADMIN'; createdAt: string }>>('/users');
    return response.data;
  },

  createUser: async (payload: { email: string; password: string; name?: string; role?: 'USER' | 'ADMIN' }) => {
    const response = await api.post('/users', payload);
    return response.data;
  },

  updateUserProfile: async (name: string): Promise<{ id: string; email: string; name: string | null; role: string; createdAt: string; updatedAt: string }> => {
    const response = await api.put<{ id: string; email: string; name: string | null; role: string; createdAt: string; updatedAt: string }>('/users/me', { name });
    return response.data;
  },
};

export interface MeetingTranscriptionItem {
  id: string;
  sessionId: string;
  status: string;
  title?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MeetingSummaryTemplate {
  title: string;
  prompt: string;
}

export interface MeetingPromptsDto {
  templates: MeetingSummaryTemplate[];
  currentTemplateIndex: number;
}

export const transcriptionsApi = {
  list: async (): Promise<MeetingTranscriptionItem[]> => {
    const response = await api.get<MeetingTranscriptionItem[]>('/transcriptions');
    return response.data;
  },

  getOne: async (id: string): Promise<MeetingTranscriptionItem & {
    finalTranscript?: string;
    finalTranscriptSegments?: { id: string; start: number; end: number; channel: number; speaker_id: string; transcript: string }[];
    transcriptManifest?: {
      version: number;
      transcript_id: string;
      created_at: string;
      duration_sec: number;
      segment_chunks: { index: number; start: number; end: number; path: string; segment_count: number }[];
    };
    transcriptResultsSummarized?: string;
    isPostProcessing?: boolean;
    rawTranscriptAvailable?: boolean;
  }> => {
    const response = await api.get<MeetingTranscriptionItem & {
      finalTranscript?: string;
      finalTranscriptSegments?: { id: string; start: number; end: number; channel: number; speaker_id: string; transcript: string }[];
      transcriptManifest?: {
        version: number;
        transcript_id: string;
        created_at: string;
        duration_sec: number;
        segment_chunks: { index: number; start: number; end: number; path: string; segment_count: number }[];
      };
      transcriptResultsSummarized?: string;
      isPostProcessing?: boolean;
      rawTranscriptAvailable?: boolean;
    }>(`/transcriptions/${id}`);
    return response.data;
  },

  getTranscriptChunk: async (
    id: string,
    chunkIndex: number,
  ): Promise<{
    version: number;
    transcript_id: string;
    index: number;
    start: number;
    end: number;
    segments: { id: string; start: number; end: number; channel: number; speaker_id: string; transcript: string }[];
  }> => {
    const response = await api.get<{
      version: number;
      transcript_id: string;
      index: number;
      start: number;
      end: number;
      segments: { id: string; start: number; end: number; channel: number; speaker_id: string; transcript: string }[];
    }>(`/transcriptions/${id}/chunks/${chunkIndex}`);
    return response.data;
  },

  listSpeakers: async (id: string): Promise<{ id: string; speakerKey: string; label: string; color?: string | null }[]> => {
    const response = await api.get<{ id: string; speakerKey: string; label: string; color?: string | null }[]>(`/transcriptions/${id}/speakers`);
    return response.data;
  },

  updateSpeaker: async (
    id: string,
    speakerId: string,
    payload: { label?: string; color?: string; userId?: string | null },
  ): Promise<{ id: string; speakerKey: string; label: string; color?: string | null }> => {
    const response = await api.patch<{ id: string; speakerKey: string; label: string; color?: string | null }>(
      `/transcriptions/${id}/speakers/${speakerId}`,
      payload,
    );
    return response.data;
  },

  downloadTranscript: async (id: string): Promise<void> => {
    const response = await api.get<Blob>(`/transcriptions/${id}/download`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/plain; charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${id}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  },

  abortSession: async (sessionId: string): Promise<{ ok: boolean }> => {
    const response = await api.delete<{ ok: boolean }>(`/transcriptions/sessions/${sessionId}`);
    return response.data;
  },

  delete: async (id: string): Promise<{ ok: boolean }> => {
    const response = await api.delete<{ ok: boolean }>(`/transcriptions/${id}`);
    return response.data;
  },

  generateSummary: async (
    id: string,
    templateIndex?: number,
  ): Promise<{ ok: boolean }> => {
    const response = await api.post<{ ok: boolean }>(`/transcriptions/${id}/summary`, {
      templateIndex,
    });
    return response.data;
  },
};

export const meetingPromptsApi = {
  getMyPrompts: async (): Promise<MeetingPromptsDto> => {
    const response = await api.get<MeetingPromptsDto>('/meeting-prompts');
    return response.data;
  },

  updateMyPrompts: async (payload: MeetingPromptsDto): Promise<MeetingPromptsDto> => {
    const response = await api.put<MeetingPromptsDto>('/meeting-prompts', payload);
    return response.data;
  },
};

export const openaiApi = {
  summarizeFile: async (file: File, message: string): Promise<{ success: boolean; summary?: string; error?: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('message', message);
    const response = await api.post<{ success: boolean; summary?: string; error?: string }>('/openai/summarizefile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export interface InstallerItem {
  id: string;
  platform: string;
  installerType: string;
  fileName: string;
  fileUrl: string;
  createdAt: string;
  updatedAt: string;
}

/** @deprecated Use InstallerItem */
export type DistributionInfo = InstallerItem;

export const distributionApi = {
  getStatus: async (): Promise<{ installers: { id: string; platform: string; installerType: string; fileName: string }[] }> => {
    const response = await api.get<{ installers: { id: string; platform: string; installerType: string; fileName: string }[] }>('/distribution/status');
    return response.data;
  },

  getDownloadLink: async (installerId: string): Promise<{ downloadUrl: string; fileName: string }> => {
    const response = await api.get<{ downloadUrl: string; fileName: string }>('/distribution/download-link', {
      params: { id: installerId },
    });
    return response.data;
  },

  getAllInstallers: async (): Promise<InstallerItem[]> => {
    const response = await api.get<InstallerItem[]>('/distribution');
    return response.data;
  },

  uploadDesktopApp: async (file: File, platform: string, installerType: string): Promise<InstallerItem> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('platform', platform);
    formData.append('installerType', installerType);
    const response = await api.post<InstallerItem>('/distribution', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteDesktopApp: async (installerId: string): Promise<{ ok: boolean }> => {
    const response = await api.delete<{ ok: boolean }>(`/distribution/${installerId}`);
    return response.data;
  },
};

export default api;
